/**
 * common.js
 */

import React from 'react';
import {
  styled,
  Slide,
  Box,
  Button,
  Chip,
  Grid,
  Typography,
} from '@mui/material';
import { add, format } from 'date-fns';
import { DISTANCE_UNIT_NAME, convertDistance } from 'utils/distance';
import { formatSecondsAsTime } from 'utils/datetime';
import FeatureBox from 'components/feature-box';
import Icon from 'components/icon';
import { UserContext } from 'contexts/user/user-context';
import { TRACKING_STATE, TRACK_TYPE_LABEL, TRACK_TYPE_ICON } from '../tracking-common';
import { LiveTrackingContext } from '../live-tracking/live-tracking-context';
import { getMarkerTimeOnTrack } from '../map/map-common';
import { MapTypeControl, CurrentLocationControl } from '../map/map-controls';
import { TrackingDrawerContext } from './drawer-context';


export const actionButtonHeight = 44;
export const controlBoxHeight = actionButtonHeight + (3 * 8 * 2);
export const headerHeight = 52;


export const DRAWER_TYPE = {
  CONTROL: 'control',
  MARKER: 'marker',
  POINT: 'point',
}


export const Subheading = styled(Typography)(({ theme }) => ({
  fontSize: theme.typography.sizes[1],
  color: theme.palette.sys.onSurfaceVariant,
  marginBottom: theme.spacing(1),
}));


export const MapControls = (props) => {
  const { isTrackingOpen } = React.useContext(LiveTrackingContext);

  return (
    <Box 
      onClick={(e) => e.stopPropagation()}
      sx={{ 
        position: 'absolute', 
        bottom: 68, 
        right: 0, 
        display: 'flex', 
        flexDirection: 'column', 
      }}
    >
      <MapTypeControl />
      {isTrackingOpen && <CurrentLocationControl />}
    </Box>
  )
}


export const DrawerHeader = (props) => {
  const {
    backgroundColor,
    children,
  } = props;

  return (
    <Box sx={{ 
      height: headerHeight, 
      backgroundColor,
      display: 'flex',
      position: 'relative',
      cursor: 'pointer',
    }}>
      <MapControls />
      {children}
    </Box>
  );
}


export const DrawerControlButton = (props) => {
  const { 
    iconName,
    sx,
    ...rest
  } = props;


  return (
    <Button
      variant='contained'
      startIcon={<Icon type={iconName} />}
      sx={{
        height: actionButtonHeight,
        mx: 1,
        px: 2.5,
        '& .MuiSvgIcon-root': iconName 
          ? {
            scale: '1.5',
            mr: 0.5
          }
          : undefined,
        ...sx,
      }}
      {...rest}
    />
  )
}


export const DrawerControlBar = (props) => {
  const { actions, leftActions, menu } = props;

  return (
    <Box sx={{ display: 'flex', pt: 4, }}>
      <Box sx={{ flexBasis: actionButtonHeight }}>
        {leftActions}
      </Box>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', }}>
        {actions}
      </Box>
      <Box sx={{ width: actionButtonHeight, ml: 1 }}>
        {menu}
      </Box>
    </Box>
  )
}


export const DrawerBase = (props) => {
  const {
    isActive,
    header,
    content,
    controls,
    canCollapse = true,
    disableLoadingUI,
  } = props;
  const { trackingState } = React.useContext(LiveTrackingContext);
  const {
    isDrawerLoading,
    isDrawerExpanded,
    setIsDrawerExpanded,
    setDrawerExpansionHeight,
  } = React.useContext(TrackingDrawerContext);
  const [contentHeight, setContentHeight] = React.useState(0);
  const contentRef = React.useRef();
  const collapsedHeight = trackingState === TRACKING_STATE.ACTIVE
    ? controlBoxHeight
    : 0;
  const drawerExpansionHeight = (isDrawerExpanded || !canCollapse) ? contentHeight : collapsedHeight;


  // handle height changes to content container
  React.useLayoutEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.offsetHeight);
      const resizeObserver = new ResizeObserver(() => {
        setContentHeight(contentRef.current.offsetHeight);
      });
      resizeObserver.observe(contentRef.current);
      return () => resizeObserver.disconnect();
    }
  }, []);


  // update the context drawer height so we can adjust the map height
  React.useEffect(() => {
    if (isActive) {
      setDrawerExpansionHeight(drawerExpansionHeight);
    }
  }, [isActive, drawerExpansionHeight, setDrawerExpansionHeight]);


  return (
    <Slide direction='up' in={isActive} appear={false}>
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>

        {/* header */}
        <Box onClick={() => canCollapse ? setIsDrawerExpanded(!isDrawerExpanded) : null}>
          {header}
        </Box>

        {/* content */}        
        <FeatureBox loading={isDrawerLoading && !disableLoadingUI} spinnerSize={60} style={{ height: '100%' }}>
          <Box sx={{
            backgroundColor: 'background.paper', 
            transition: (theme) => `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeInOut}`,
            position: 'relative',
            overflow: 'hidden',
            height: drawerExpansionHeight,
          }}>
            <Box 
              ref={contentRef} 
              sx={{ 
                p: 3,
                position: 'absolute',
                bottom: 0,
                left: 0, 
                right: 0,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box sx={{ maxWidth: 600, width: '100%' }}>
                {content}
                {controls}
              </Box>
            </Box>
          </Box>
        </FeatureBox>

      </Box>
    </Slide>
  )
}


export const GridCell = (props) => {
  const {
    iconId,
    content,
    sx,
    ...rest
  } = props;


  return (
    <Box className='flex-hc' {...rest} sx={{ lineHeight: 1.2, ...sx }}>
      <Icon type={iconId} sx={{ mr: 1, color: 'sys.primary' }} />
      <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {content}
      </Box>
    </Box>
  )
}


const EstimateLabel = () => {
  return <Chip
    color='warning'
    variant='outlined'
    size='small'
    label='est'
    sx={{ ml: 0.5 }}
  />;
}


export const DurationGridCell = (props) => {
  const { seconds, label, estimate } = props;
  const formattedTime = (seconds || seconds === 0)
    ? `${formatSecondsAsTime(seconds)} ${label || 'duration'}`
    : '-'

  return (
    <GridCell
      iconId='timer'
      content={<>
        {formattedTime}
        {estimate && <EstimateLabel />}
      </>}
    />
  )
}


export const MarkerElapsedGridCell = (props) => {
  const { track, marker } = props;

  const elapsedTimeOnTrack = React.useMemo(() => {
    if (track && marker) {
      return getMarkerTimeOnTrack(track, marker);
    }
  }, [track, marker]);

  return <DurationGridCell
    seconds={elapsedTimeOnTrack.value}
    label='elapsed'
    estimate={elapsedTimeOnTrack.isEstimate}
  />
}


export const MarkerTimeGridCell = (props) => {
  const { track, marker } = props;

  const elapsedTimeOnTrack = React.useMemo(() => {
    if (track && marker) {
      return getMarkerTimeOnTrack(track, marker);
    }
  }, [track, marker]);

  return <GridCell
    iconId='event'
    content={<>
      {isNaN(elapsedTimeOnTrack.value)
        ? 'n/a'
        : format(add(track.dateTimeCreated, { seconds: elapsedTimeOnTrack.value }), 'M/d/yyyy h:mm aa')}
      {/* Could add an "est" label but not necessary with minute resolution}
      {elapsedTimeOnTrack.isEstimate && <EstimateLabel />}
      */}
    </>}
  />
}


export const DistanceGridCell = (props) => {
  const { distance } = props;
  const formattedDistance = (distance || distance === 0)
    ? `${Math.round(convertDistance(distance, DISTANCE_UNIT_NAME.METERS, DISTANCE_UNIT_NAME.YARDS))} yards`
    : '-'

  return (
    <GridCell
      iconId='distance'
      content={formattedDistance}
    />
  )
}


export const SelectedTrackGrid = (props) => {
  const { track } = props;
  const { user } = React.useContext(UserContext);
  const trackUserName = track.userName
    ? track.userName  // track username has been set
    : (track.id || !user)
      ? 'Anonymous'  // track has been persisted with no user, or track hasn't been persisted and current user is anon
      : user.name  // track hasn't been persisted and current user isn't anon
  const lastPoint = track.points.length > 0 ? track.points[track.points.length - 1] : null;
  

  return (
    <Box sx={{ mt: 3 }}>
      <Subheading>SELECTED TRACK</Subheading>
      <Grid container spacing={1}>
        <Grid item xs={6}>
          <GridCell
            iconId='info'
            content={TRACK_TYPE_LABEL[track.type]}
          />
        </Grid>
        <Grid item xs={6}>
          <GridCell
            iconId={TRACK_TYPE_ICON[track.type]}
            content={trackUserName}
          />
        </Grid>
        <Grid item xs={6}>
          <DurationGridCell seconds={lastPoint.elapsedSeconds} />
        </Grid>
        <Grid item xs={6}>
          <DistanceGridCell distance={lastPoint.distanceMeters} />
        </Grid>
      </Grid>
    </Box>
  );
}


export const LocationAccuracyChip = (props) => {
  const { accuracy } = props;

  let chipClass, label;
  if (accuracy === undefined || accuracy === null || accuracy < 0) {
    label = 'Unknown';
  } else if (accuracy <= 4) {
    label = 'Excellent';
    chipClass = 'chip-ok chip-font-ok';
  } else if (accuracy <= 7) {
    label = 'Good';
    chipClass = 'chip-ok chip-font-ok';
  } else if (accuracy <= 15) {
    label = 'Fair';
    chipClass = 'chip-warning';
  } else if (accuracy <= 30) {
    label = 'Poor';
    chipClass = 'chip-error chip-font-error';
  } else {
    label = 'Very Poor';
    chipClass = 'chip-error chip-font-error';
  }

  return <Chip
    size='small' 
    label={label}
    sx={{ px: 1 }}
    className={chipClass}
  />
}